import { Component, OnInit, ViewChild, OnDestroy, Inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { JwtAuthService } from '../../../shared/services/auth/jwt-auth.service';
import { ProfileType } from '../../../shared/enums/profile-type.enum';
import { config } from 'config';
import {
  FacilityAddDialogComponent,
  FacilityDialogData,
} from './dialogs/facility-add-dialog.component';
import { AlertService } from 'app/shared/services/alert/alert.service';
import { REFERRAL_GROUPS, ReferralGroup, ReferralGroupService } from '../../../shared/constants/referral-groups.constants';
import { ExportDialogComponent, ExportDialogData } from '../../../shared/components/export-dialog/export-dialog.component';
import { FacilityService } from 'app/shared/services/facility.service';
import { ApprovalConfirmDialogComponent } from './dialogs/approval-confirm-dialog.component';
import { ActivityCheckDialogComponent } from './dialogs/activity-check-dialog.component';
import { OtpDialogModule } from '../../../shared/components/otp-dialog/otp-dialog.module';

export interface Facility {
  ID: number;
  FacilityID: string;
  activeReferralGroupsID: string;
  sendSMS: string;
  sendEmail: string;
  smsTemplates: string | null;
  rule: string;
  pharmacyDomain: string | null;
  pharmacyLocationids: string | null;
  medicalClinicEmail: string;
  medicalClinicLogPassword: string;
  practiceSoftware: string;
  InstallDate: string;
  ClinicName: string;
  scriptsCCemail: string | null;
  updated_at: string | null;
  created_at: string | null;
  street_address?: string;
  city?: string;
  state?: string;
  postcode?: string;
  practice_manager_name?: string;
  practice_manager_email?: string;
  practice_manager_mobile?: string;
  it_technical_name?: string;
  it_technical_email?: string;
  it_technical_mobile?: string;
  referral_group_requester_name?: string;
  referral_group_requester_email?: string;
  referral_group_requester_phone?: string;
  approved?: boolean;
  agreements_at?: string;
  performer_ids?: number[];
  notes?: string;
  account_manager_name?: string;
  last_request: {
    to_facility: {
      facility_id: string;
      patient_name: string;
      date_received: string;
      inactivity: string;
    } | null;
    to_group: {
      facility_id: string;
      destination: string;
      test_requested: string;
      patient_name: string;
      performer: string;
      date_received: string;
      date_sent: string;
      inactivity: string;
    } | null;
    status: 'Red' | 'Green' | 'Yellow' | 'Unknown';
  };
}

export interface FacilityResponse {
  current_page: number;
  data: Facility[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
}

export interface ActivityCheckResponse {
  Last_Request_From_Facility?: {
    DateReceived: string;
    FacilityID: string;
    Destination?: string;
    DayDifference: number;
  };
  Last_Request_To_You?: {
    DateReceived: string;
    FacilityID: string;
    Destination?: string;
    DayDifference: number;
  } | null;
  Status: 'Red' | 'Green' | 'Yellow' | 'Unknown';
}

@Component({
    selector: 'app-facility-maintenance',
    templateUrl: './facility-maintenance.component.html',
    styleUrls: ['./facility-maintenance.component.scss'],
    standalone: false
})
export class FacilityMaintenanceComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = [
    'status',
    'FacilityID',
    'ClinicName',
    'accountManager',
    'notes',
    'lastRequestFromFacility',
    'lastRequestToGroup',
    'actions',
  ];

  dataSource = new MatTableDataSource<Facility>();
  isLoading = false;
  searchTerm = '';
  searchType = 'facility_id'; // Default search type
  selectedGroupFilter = '';
  isActivityCheck = false;

  report: any = null;
  customDateFrom: string = '';
  customDateTo: string = '';

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  // Pagination
  totalItems = 0;
  pageSize = 50;
  currentPage = 1;
  pageSizeOptions = [50, 100, 150, 200, 250];

  // Role-based access control
  isRoleBasedUser = false;
  userReferralGroupId: number | null = null;
  canAddFacility = true;
  canEditFacility = true;
  canDeleteFacility = true;
  canExport = true;

  referralGroups: ReferralGroup[] = REFERRAL_GROUPS;

  constructor(
    private http: HttpClient,
    private authService: JwtAuthService,
    private dialog: MatDialog,
    private router: Router,
    private alertService: AlertService,
    private facilityService: FacilityService,
    private referralGroupService: ReferralGroupService,
  ) {
    this.setupSearch();
  }

  ngOnInit(): void {
    this.searchType = 'facility_id';

    this.setupRoleBasedAccess();
    this.checkRouteMode();
    this.loadFacilities();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private setupSearch(): void {
    this.searchSubject
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(searchTerm => {
        this.searchTerm = searchTerm;
        this.currentPage = 1;
        this.loadFacilities();
      });
  }

  checkRouteMode(): void {
    this.isActivityCheck = this.router.url.includes('activity-check');
    if (this.isActivityCheck && !this.isRoleBasedUser) {
      this.displayedColumns = this.displayedColumns.filter(col => col !== 'group');
    }
  }

  setupRoleBasedAccess(): void {
    const userRole = this.authService.getRole();
    const user = this.authService.getUser();

    this.isRoleBasedUser = userRole !== ProfileType.Admin && userRole !== ProfileType.SuperAdmin;

    // Set permissions based on role
    switch (userRole) {
      case ProfileType.Admin:
      case ProfileType.SuperAdmin:
        this.canAddFacility = true;
        this.canEditFacility = true;
        this.canDeleteFacility = true;
        this.canExport = true;
        break;
      case ProfileType.RadiologyUser:
        this.canAddFacility = true;
        this.canEditFacility = true;
        this.canDeleteFacility = true;
        this.canExport = true;
        break;
      default:
        this.canAddFacility = false;
        this.canEditFacility = false;
        this.canDeleteFacility = false;
        this.canExport = false;
    }

    if (this.authService.isRadiology()) {
      this.userReferralGroupId = this.authService.getReferralGroupId();
    }

    // If role-based user, set group filter and remove group column
    if (this.isRoleBasedUser && this.userReferralGroupId) {
      this.selectedGroupFilter = this.userReferralGroupId.toString();
      this.displayedColumns = this.displayedColumns.filter(col => col !== 'group');
    }
  }

  get isSearchDisabled(): boolean {
    return ['facility_to_approve', 'facility_to_activate', 'facility_agreement_pending'].includes(this.searchType);
  }

  onSearchTypeChange(event?: any): void {
    if (event?.value) {
      this.searchType = event.value;

      if(['facility_to_approve', 'facility_to_activate', 'facility_agreement_pending'].includes(this.searchType)) this.loadFacilities();
    }

    // Clear search when switching types
    this.searchTerm = '';
    this.currentPage = 1;
    this.loadFacilities();
  }

  loadFacilities(): void {
    if (!this.isRoleBasedUser && !this.selectedGroupFilter) return;

    this.isLoading = true;

    let url = `${config.apiUrl1}facility/get?page=${this.currentPage}&per_page=${this.pageSize}`;

    if (this.selectedGroupFilter) {
      url += `&referral_group_id=${this.selectedGroupFilter}`;
    }

    if (this.searchTerm) {
      url += `&search_term=${encodeURIComponent(this.searchTerm)}`;
      url += `&search_type=${this.searchType}`;
    } else if(this.searchType === 'facility_to_approve') {
      url += `&search_type=facility_to_approve`;
    } else if(this.searchType === 'facility_agreement_pending') {
      url += `&search_type=facility_agreement_pending`;
    } else if(this.searchType === 'facility_to_activate') {
      url += `&search_type=facility_to_activate`;
    }

    this.http.get<FacilityResponse>(url).subscribe({
      next: (response: FacilityResponse) => {
        // Clean clinic names
        const cleanedData = response.data.map(facility => ({
          ...facility,
          ClinicName: this.cleanClinicName(facility.ClinicName),
        }));

        this.dataSource.data = cleanedData;
        this.totalItems = response.total;
        this.isLoading = false;

        this.facilityService.getReport({ referral_group_id: this.selectedGroupFilter }).subscribe({
          next: (response) => {
            this.report = response;
          },
          error: (error) => {
            this.alertService.notifyError('Error fetching facility report');
          }
        });
      },
      error: error => {
        this.isLoading = false;
        this.alertService.notifyError('Error loading facilities');
        console.error('Error loading facilities:', error);
      },
    });
  }

   onCustomDateRangeChange() {
    if (!this.customDateFrom || !this.customDateTo || !this.selectedGroupFilter) {
      return;
    }
    this.facilityService.getReport({
      referral_group_id: this.selectedGroupFilter,
      date_from: this.customDateFrom, 
      date_to: this.customDateTo 
    }).subscribe({
      next: (response) => {
        this.report.count_received_custom = response.count_received_custom || 0;
        this.report.count_sent_custom = response.count_sent_custom || 0;
        this.report.last_DateReceived_custom = response.last_DateReceived_custom || '';
        this.report.last_DateSent_custom = response.last_DateSent_custom || '';
      },
      error: () => {
        this.report.count_received_custom = 0;
        this.report.count_sent_custom = 0;
        this.report.last_DateReceived_custom = '';
        this.report.last_DateSent_custom = '';
      }
    });
  }

  onSearchInput(event: any): void {
    if (['facility_to_approve', 'facility_to_activate', 'facility_agreement_pending'].includes(this.searchType)) return;
    
    this.searchSubject.next(event.target.value);
  }

  getSearchPlaceholder(): string {
    if (this.searchType === 'facility_id') {
      return 'Search by Facility ID';
    } else if (['facility_to_approve', 'facility_to_activate', 'facility_agreement_pending'].includes(this.searchType)) {
      return '';
    } else {
      return 'Search by Facility Name';
    }
  }

  applyFilter(): void {
    this.currentPage = 1;
    this.loadFacilities();
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.loadFacilities();
  }

  checkActivity(facility: Facility): void {
    const openDialog = (report: any) => {
      this.dialog.open(ActivityCheckDialogComponent, {
        width: '90vw',
        maxWidth: '1400px',
        maxHeight: '85vh',
        data: {
          facility: facility,
          activityData: facility.last_request,
          referralGroups: this.referralGroups,
          activityPeriods: report,
        }
      });
    };

    if (facility.FacilityID) {
      this.facilityService.getActivity({ id: facility.ID, referral_group_id: facility.activeReferralGroupsID }).subscribe({
        next: (response) => {
          facility.last_request = response;
        },
        error: (error) => {
          console.error('Error fetching activity:', error);
          this.alertService.notifyError('Error fetching facility activity');
        }
      });

      this.facilityService.getReport({ facility_id: facility.FacilityID }).subscribe({
        next: (response) => {
          openDialog(response);
        },
        error: (error) => {
          console.error('Error fetching report:', error);
          this.alertService.notifyError('Error fetching facility report');
          openDialog(null);
        }
      });
      
    } else {
      openDialog(null);
    }
  }

  deleteFacility(facility: Facility): void {
    const confirmMessage = `Are you sure you want to delete facility "${facility.ClinicName}" (${facility.FacilityID})?`;

    if (confirm(confirmMessage)) {
      const url = `${config.apiUrl1}facility/delete/${facility.ID}`;
      const body = {
        referral_group_id: facility.activeReferralGroupsID,
      };

      this.http.post(url, body).subscribe({
        next: () => {
          this.alertService.notifySuccess('Facility deleted successfully');
          this.loadFacilities();
        },
        error: error => {
          this.alertService.notifyError('Error deleting facility');
          console.error('Error deleting facility:', error);
        },
      });
    }
  }

  exportToCSV(): void {
    const csvData = this.dataSource.data.map(facility => ({
      'Facility ID': facility.FacilityID,
      'Facility Name': facility.ClinicName,
      'Referral Group': this.getReferralGroupName(facility.activeReferralGroupsID),
      'Practice Software': facility.practiceSoftware,
      'Status': facility.last_request?.status || 'Unknown',
      'Install Date': this.formatInstallDate(facility.InstallDate),
      'Last Request Received': facility.last_request?.to_facility?.date_received
        ? this.formatDate(facility.last_request.to_facility.date_received)
        : ' ',
      'Last Request Sent': facility.last_request?.to_group?.date_sent
        ? this.formatDate(facility.last_request.to_group.date_sent)
        : ' '
    }));

    const csvContent = this.convertToCSV(csvData);
    const filename = `facilities_${new Date().toISOString().split('T')[0]}.csv`;
    this.downloadCSV(csvContent, filename);
    this.alertService.notifySuccess('Facilities exported successfully');
  }

  private convertToCSV(data: any[]): string {
    if (data.length === 0) return '';

    const headers = Object.keys(data[0]);
    const rows = data.map(row => headers.map(header => `"${row[header] || ''}"`).join(','));

    return [headers.join(','), ...rows].join('\n');
  }

  private downloadCSV(csvContent: string, filename: string): void {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  refreshData(): void {
    this.loadFacilities();
  }

  // Utility methods
  getReferralGroupName(groupId: string | number): string {
    const id = typeof groupId === 'string' ? parseInt(groupId) : groupId;
    const group = this.referralGroups.find(g => g.value === id);
    return group ? group.label : 'Unknown';
  }

  getStatusColor(status: string): string {
    switch (status?.toLowerCase()) {
      case 'green':
        return '#4caf50';
      case 'red':
        return '#f44336';
      case 'yellow':
        return '#ff9800';
      default:
        return '#9e9e9e';
    }
  }

  getGroupColor(referralGroupId: string | number): string {
    const colors: { [key: string]: string } = {
      '1': '#4CAF50',
      '2': '#2196F3', 
      '3': '#FF9800',
      '4': '#9C27B0',
      '5': '#F44336'
    };

    const groupId = referralGroupId?.toString() || '';
    return colors[groupId] || '#757575';
  }

  getStatusIcon(status: string): string {
    switch (status?.toLowerCase()) {
      case 'green':
        return 'check_circle';
      case 'red':
        return 'error';
      case 'yellow':
        return 'warning';
      default:
        return 'help';
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }

  formatInstallDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-AU', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }

  getDayDifferenceText(days: number): string {
    if (!days && days !== 0) return '';
    if (days < 1) return 'Today';
    else if (days === 1) return '1 day ago';
    else if (days < 30) return `${Math.floor(days)} days ago`;
    else if (days < 365) {
      const months = Math.floor(days / 30);
      return `${months} month${months > 1 ? 's' : ''} ago`;
    } else {
      const years = Math.floor(days / 365);
      return `${years} year${years > 1 ? 's' : ''} ago`;
    }
  }

  getInstallDaysAgo(facility: Facility): string {
    if (!facility.InstallDate) return '';
    try {
      const install = new Date(facility.InstallDate);
      const now = new Date();
      const diffTime = now.getTime() - install.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return this.getDayDifferenceText(diffDays);
    } catch (error) {
      return '';
    }
  }

  cleanClinicName(name: string): string {
    if (!name) return '';
    return name.replace(/\t/g, ' ').replace(/\s+/g, ' ').trim();
  }

  trackByFacilityId(index: number, facility: Facility): number {
    return facility.ID;
  }

  Math = Math; // Expose Math to template

  openAddFacilityDialog(): void {
    const dialogData: FacilityDialogData = {
      facility: null,
      referralGroups: this.referralGroups,
      isEdit: false,
      isRoleBasedUser: this.isRoleBasedUser,
      userReferralGroupId: this.userReferralGroupId,
      userReferralGroupName: this.authService.isRadiology() ? this.authService.getReferralGroupName() : null,
    };

    const dialogRef = this.dialog.open(FacilityAddDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true,
      autoFocus: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.createFacility(result);
      }
    });
  }

  editFacility(facility: Facility): void {
    const dialogData: FacilityDialogData = {
      facility: facility,
      referralGroups: this.referralGroups,
      isEdit: true,
      isRoleBasedUser: this.isRoleBasedUser,
      userReferralGroupId: this.userReferralGroupId,
      userReferralGroupName: this.authService.isRadiology() ? this.authService.getReferralGroupName() : null,
    };

    const dialogRef = this.dialog.open(FacilityAddDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: dialogData,
      disableClose: true,
      autoFocus: true,
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        result.ID = facility.ID;
        result.approved = facility.approved;
        result.agreement_at = facility.agreements_at;
        this.updateFacility(result);
      }
    });
  }

  private createFacility(facilityData: any): void {
    this.isLoading = true;

    this.facilityService.createFacility(facilityData).subscribe({
      next: response => {
        this.isLoading = false;

        let successMsg = response.message || 'Facility created successfully';

        if (response.email_error && response.email_error.length > 0) {
          successMsg += `. However, some emails failed: ${response.email_error.join(', ')}`;
        }

        this.alertService.notifySuccess(successMsg);
        this.loadFacilities(); // Refresh the table
      },
      error: error => {
        this.isLoading = false;
        console.error('Error creating facility:', error);

        if (error.error?.message) {
          this.alertService.notifyError(`Error creating facility: ${error.error.message}`);
        } else if (error.error?.errors) {
          const errors = error.error.errors;
          const errorMessages: string[] = [];

          for (const key in errors) {
            if (errors.hasOwnProperty(key)) {
              const value = errors[key];
              if (Array.isArray(value)) {
                errorMessages.push(...value);
              } else if (typeof value === 'string') {
                errorMessages.push(value);
              }
            }
          }

          this.alertService.notifyError(`Validation errors: ${errorMessages.join(', ')}`);
        } else {
          this.alertService.notifyError('Error creating facility. Please try again.');
        }
      }
    });
  }

  private updateFacility(facilityData: any): void {
    this.isLoading = true;
    this.facilityService.updateFacility(facilityData.ID ?? facilityData.id, facilityData).subscribe({
      next: response => {
        this.isLoading = false;
        this.alertService.notifySuccess('Facility updated successfully');
        this.loadFacilities(); // Refresh the table
      },
      error: error => {
        this.isLoading = false;
        console.error('Error updating facility:', error);
        this.alertService.notifyError('Error updating facility. Please try again.');
      }
    });
  }

  openExportDialog(): void {
    const dialogData: ExportDialogData = {
      title: 'Facility Management',
      type: 'facility',
      referral_group_id: this.selectedGroupFilter || (this.userReferralGroupId ? this.userReferralGroupId.toString() : undefined),
      search_term: this.searchTerm || undefined,
      search_type: this.searchType || undefined
    };

    Object.keys(dialogData).forEach(key => {
      if (dialogData[key] === undefined || dialogData[key] === '') {
        delete dialogData[key];
      }
    });


    const dialogRef = this.dialog.open(ExportDialogComponent, {
      width: '500px',
      data: dialogData,
      disableClose: false
    });

    // No need to handle the result since the API call happens inside the dialog
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
      }
    });
  }

  approveFacility(facility: Facility): void {
    const dialogRef = this.dialog.open(ApprovalConfirmDialogComponent, {
      width: '400px',
      data: {
        facilityName: facility.ClinicName,
        facilityId: facility.FacilityID
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm') {
        this.confirmApproveFacility(facility);
      }
    });
  }

  private confirmApproveFacility(facility: Facility): void {
    this.isLoading = true;

    const payload = {
      facility_id: facility.ID
    };

    this.facilityService.approveFacility(payload).subscribe({
      next: response => {
        this.isLoading = false;
        this.alertService.notifySuccess('Facility approved successfully');
        this.loadFacilities();
      },
      error: error => {
        this.isLoading = false;
        console.error('Error approving facility:', error);
        this.alertService.notifyError('Error approving facility. Please try again.');
      }
    });
  }

  goToAddFacilityPage() {
    this.referralGroupService.setSelectedReferralGroup(
      this.selectedGroupFilter ? Number(this.selectedGroupFilter) : null
    );
    console.log('Navigating to add facility page with group:', this.selectedGroupFilter);
    this.router.navigate(['/facility-management-add']);
  }

  get showAgreementPendingStatus(): boolean {
    if (this.authService.isAdmin()) {
      return true;
    }
    return false;
  }
}